// «بیماران» — this student's practice patients.
//
// ── Everything here is scoped by the TOKEN, not by an argument ─────────────
//
// `myCases` takes no student id, and there is no argument anywhere in the frozen
// schema for whose data to read (core.md §3.2). The student id is the JWT `sub`,
// read in the BFF's context factory, and it is the AUTHORIZATION rather than a
// filter. Nothing in this file may grow a parameter that names an owner.
//
// ── There is no server-side search, and that is deliberate ────────────────
//
// A text search is the one query shape that becomes cross-student by dropping a
// single predicate (core.md §3.2), so the contract has none. The list is capped
// at 100 rows server-side and the screen searches, sorts and pages what it
// already holds — `filterCases` and `sortCases` below are the whole of it, and
// they are pure so tests/screens.test.jsx can drive them without a network.
//
// ── Why the FILTER is here and the SORT is not a query argument ───────────
//
// `statuses` IS a wire argument (the server can answer «فقط بایگانی» without
// reading everything), so it drives a refetch. The sort is not: reordering 100
// rows the browser is already holding must not cost a round trip, and «آخرین
// ویزیت» has to put a patient with no visits at the end, which no ORDER BY the
// contract offers would do.

import React from 'react';

import { bffQuery, bffMutation, errorCode } from '../lib/bffClient.js';
import {
  MY_CASES_QUERY,
  ANY_CASES_QUERY,
  CREATE_CASE_MUTATION,
  UPDATE_CASE_MUTATION,
  DELETE_CASE_MUTATION,
} from '../graphql/documents.js';
import { CASE_STATUSES } from '../constants/vocabulary.js';
import { refreshProfile } from './useMe.js';

export { CASE_STATUSES };

/**
 * The status filter the list opens on: فعال + پیش‌نویس (core.md §7.3).
 *
 * Archived is OUT by default and reachable by one chip. A student archives a
 * patient to stop seeing them; a list that showed them anyway would make the
 * action look broken, and the only other honest default — showing everything —
 * buries the two patients they are working on under the twenty they finished.
 */
export const DEFAULT_STATUSES = ['active', 'draft'];

/** The server cap. Past this the screen says so rather than paging silently. */
export const LIST_LIMIT = 100;

export const SORTS = ['lastVisit', 'newest', 'name'];
export const DEFAULT_SORT = 'lastVisit';

// Does this student have ANY patient, in any status? `null` = not asked yet.
// Module-level so the answer is one round trip per session, not one per mount.
let anyCache = null;

/** Test seam — forgets the cached "any patients?" answer. */
export function resetCasesCache() {
  anyCache = null;
}

/**
 * Filter the loaded page by text, client-side.
 *
 * Matches the title, the invented patient name, the student's own filing label
 * and the chief complaint — the four things a student would type to find a
 * patient they remember. `caseRef` is matched as the sticky note it is: never
 * validated, never looked up anywhere, never sent outside this app (core.md
 * §3.1).
 */
export function filterCases(cases, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return cases || [];
  return (cases || []).filter((row) =>
    [row?.title, row?.displayName, row?.caseRef, row?.chiefComplaint]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(q)),
  );
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Order the loaded page. Never mutates the array it is given.
 *
 * `lastVisit` puts a patient with NO visits last rather than first: an unvisited
 * patient has `lastEncounterAtMs` unset, which arrives as 0 or null, and a
 * naive descending sort would float every untouched draft above the visit the
 * student closed this morning.
 */
export function sortCases(cases, sort = DEFAULT_SORT) {
  const rows = [...(cases || [])];
  if (sort === 'name') {
    return rows.sort((a, b) =>
      String(a?.displayName || a?.title || '').localeCompare(
        String(b?.displayName || b?.title || ''),
        'fa',
      ),
    );
  }
  if (sort === 'newest') {
    return rows.sort((a, b) => num(b?.createdAtMs) - num(a?.createdAtMs));
  }
  return rows.sort((a, b) => {
    const av = num(a?.lastEncounterAtMs);
    const bv = num(b?.lastEncounterAtMs);
    if (av && bv) return bv - av;
    if (av) return -1;
    if (bv) return 1;
    // Neither has been visited: the newer patient is the one being worked on.
    return num(b?.createdAtMs) - num(a?.createdAtMs);
  });
}

/**
 * @param statuses  a subset of CASE_STATUSES. `[]`/null asks for all of them.
 */
export function useCases({ statuses = DEFAULT_STATUSES, limit = LIST_LIMIT } = {}) {
  const [cases, setCases] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);

  // ── "No patients at all" is a fact about ALL statuses, not the filter ──
  //
  // `statuses` is a wire argument, so an empty page says only "nothing with
  // these statuses". «بایگانی» with no archived rows must render «موردی مطابق
  // فیلتر نیست», never «هنوز بیماری نساخته‌اید» — to a student with forty
  // active patients the second one says their work is gone (core.md §7.3).
  // The list query carries no `total`, so the unfiltered answer is one more
  // tiny read — every status, one row, ids only — cached for the session and
  // invalidated by the two mutations that can change it: a create (which
  // proves it true without a round trip) and a delete.
  const [anyKnown, setAnyKnown] = React.useState(anyCache);
  const [anyNonce, setAnyNonce] = React.useState(0);

  React.useEffect(() => {
    if (anyCache !== null) {
      setAnyKnown(anyCache);
      return undefined;
    }
    let alive = true;
    bffQuery(ANY_CASES_QUERY, { limit: 1, offset: 0 })
      .then((data) => {
        anyCache = (data?.myCases || []).length > 0;
        if (alive) setAnyKnown(anyCache);
      })
      .catch((err) => {
        // Not cached: a failed probe must not be remembered as "no patients".
        // The screen falls back to what the page itself proves.
        console.warn('[useCases] any-cases probe failed:', errorCode(err) || err?.message);
        if (alive) setAnyKnown(false);
      });
    return () => {
      alive = false;
    };
  }, [anyNonce]);

  // An ARRAY in a dependency list is a new identity on every render, which would
  // refetch forever. The wire value is what actually matters, so the dependency
  // is its serialisation and the array is rebuilt from it inside the effect.
  const statusKey = (statuses && statuses.length ? [...statuses].sort() : []).join(',');

  React.useEffect(() => {
    let alive = true;
    setLoading(true);

    bffQuery(MY_CASES_QUERY, {
      statuses: statusKey ? statusKey.split(',') : null,
      limit,
      offset: 0,
    })
      .then((data) => {
        if (!alive) return;
        setCases(data?.myCases || []);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[useCases] failed:', errorCode(err) || err?.message);
        setError(err);
        // A failed read replaces the list with an ERROR state, never with an
        // empty one: "you have made no patients yet" and "we could not ask" are
        // different facts, and only the first one means "start writing".
        setCases([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [statusKey, limit, nonce]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  /** Create a patient. Resolves to the stored row so the caller can navigate. */
  const createCase = React.useCallback(
    async (input) => {
      const data = await bffMutation(CREATE_CASE_MUTATION, { input });
      // A stored row proves the answer without asking again.
      anyCache = true;
      setAnyKnown(true);
      reload();
      // `caseCount` on the profile just moved; the profile screen prints it.
      refreshProfile();
      return data?.createCase || null;
    },
    [reload],
  );

  /**
   * Archive / unarchive, optimistically.
   *
   * The row moves out of (or into) the current filter, so the local list is
   * patched before the round trip and the screen repaints at once; a failure
   * re-reads from the server rather than trying to reverse the patch, because
   * the authoritative answer is one query away and a hand-rolled undo is how a
   * list ends up showing a state the store never held.
   */
  const setCaseStatus = React.useCallback(
    async (caseId, status) => {
      setCases((prev) => prev.map((row) => (row.id === caseId ? { ...row, status } : row)));
      try {
        const data = await bffMutation(UPDATE_CASE_MUTATION, { caseId, input: { status } });
        reload();
        return data?.updateCase || null;
      } catch (err) {
        reload();
        throw err;
      }
    },
    [reload],
  );

  /**
   * Delete a patient, their history, visits, threads and messages, in one
   * transaction.
   *
   * There is no undo and no archive-instead fallback — `status: 'archived'` is
   * what archiving is for — and it refunds nothing: the پارسه was spent in
   * another service when the answer was produced. The screen says both of those,
   * with the counts, before it calls this.
   */
  const deleteCase = React.useCallback(
    async (caseId) => {
      await bffMutation(DELETE_CASE_MUTATION, { caseId });
      // The last patient may have just gone: forget the cached answer and ask
      // again, rather than guessing from a page that is filtered.
      anyCache = null;
      setAnyKnown(null);
      setAnyNonce((n) => n + 1);
      reload();
      refreshProfile();
    },
    [reload],
  );

  // A page WITH rows proves "at least one" without the probe; an empty page
  // proves nothing and waits for it.
  const hasAny = cases.length > 0 || anyKnown === true;
  const anySettled = cases.length > 0 || anyKnown !== null;

  return {
    cases,
    // Is there at least one patient in ANY status? Drives the difference
    // between «هنوز بیماری نساخته‌اید» and «موردی مطابق فیلتر نیست».
    hasAny,
    // `loading` below already covers the page; this is the probe's own flag,
    // so a screen never renders the empty state on an unanswered question.
    anyLoading: !anySettled,
    loading,
    error,
    reload,
    createCase,
    setCaseStatus,
    deleteCase,
    // The server capped the answer. There is no `total` on this query, so this
    // is the only honest "there may be more" the shape can support — and the
    // recovery the screen offers is «جستجو کنید», not a pager into rows the
    // query never returned.
    capped: cases.length >= limit,
  };
}

export default useCases;
